import * as React from 'react';
import * as ToolbarPrimitive from '@radix-ui/react-toolbar';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cva, type VariantProps } from 'class-variance-authority';
import { ChevronDown } from 'lucide-react';
import {
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuSeparator,
} from 'src/components/ui/editor/ui/dropdown-menu';
import { Separator } from 'src/components/ui/editor/ui/separator';
import {
  TooltipEditor,
  TooltipEditorProvider,
  TooltipEditorTrigger,
} from 'src/components/ui/editor/ui/tooltip-editor';
import { cn } from 'src/lib/utils';
import { forwardRef } from 'react';

export function ToolbarEditor({
  className,
  ...props
}: React.ComponentProps<typeof ToolbarPrimitive.Root>) {
  return (
    <ToolbarPrimitive.Root
      className={cn('relative flex select-none items-center', className)}
      {...props}
    />
  );
}

export const ToolbarEditorToggleGroup = forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof ToolbarPrimitive.ToolbarToggleGroup>
>(({ className, ...props }, ref) => {
  return (
    <ToolbarPrimitive.ToolbarToggleGroup
      ref={ref}
      className={cn('flex items-center', className)}
      {...props}
    />
  );
});

export function ToolbarEditorLink({
  className,
  ...props
}: React.ComponentProps<typeof ToolbarPrimitive.Link>) {
  return (
    <ToolbarPrimitive.Link
      className={cn('font-medium underline underline-offset-4', className)}
      {...props}
    />
  );
}

export function ToolbarEditorSeparator({
  className,
  ...props
}: React.ComponentProps<typeof ToolbarPrimitive.Separator>) {
  return (
    <ToolbarPrimitive.Separator
      className={cn('mx-2 my-1 w-px shrink-0 bg-border', className)}
      {...props}
    />
  );
}

// From toggleVariants
const toolbarButtonVariants = cva(
  "inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium text-sm outline-none transition-[color,box-shadow] hover:bg-muted hover:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-checked:bg-accent aria-checked:text-accent-foreground aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    defaultVariants: {
      size: 'default',
      variant: 'default',
    },
    variants: {
      size: {
        default: 'h-9 min-w-9 px-2',
        lg: 'h-10 min-w-10 px-2.5',
        sm: 'h-8 min-w-8 px-1.5',
      },
      variant: {
        default: 'bg-transparent',
        outline:
          'border border-input bg-transparent shadow-xs hover:bg-accent hover:text-accent-foreground',
      },
    },
  },
);

type ToolbarEditorButtonProps = {
  isDropdown?: boolean;
  // A boolean renders a toggle; undefined renders a plain button.
  pressed?: boolean;
} & Omit<React.ComponentPropsWithoutRef<typeof ToolbarPrimitive.ToggleItem>, 'asChild' | 'value'> &
  VariantProps<typeof toolbarButtonVariants>;

// The ref lands on the toggle group or the button, whichever renders.
const ToolBarEditorButton = forwardRef<HTMLButtonElement, ToolbarEditorButtonProps>(
  ({ children, className, isDropdown, pressed, size = 'sm', variant, ...props }, ref) => {
    return typeof pressed === 'boolean' ? (
      <ToolbarEditorToggleGroup
        ref={ref as unknown as React.Ref<HTMLDivElement>}
        disabled={props.disabled}
        value="single"
        type="single"
      >
        <ToolbarEditorToggleItem
          className={cn(
            toolbarButtonVariants({
              size,
              variant,
            }),
            isDropdown && 'justify-between gap-1 pr-1',
            className,
          )}
          value={pressed ? 'single' : ''}
          {...props}
        >
          {isDropdown ? (
            <>
              <div className="flex flex-1 items-center gap-2 whitespace-nowrap">{children}</div>
              <div>
                <ChevronDown className="size-3.5 text-muted-foreground" data-icon />
              </div>
            </>
          ) : (
            children
          )}
        </ToolbarEditorToggleItem>
      </ToolbarEditorToggleGroup>
    ) : (
      <ToolbarPrimitive.Button
        ref={ref}
        className={cn(
          toolbarButtonVariants({
            size,
            variant,
          }),
          isDropdown && 'pr-1',
          className,
        )}
        {...props}
      >
        {children}
      </ToolbarPrimitive.Button>
    );
  },
);

export const ToolbarEditorButton = withTooltip(ToolBarEditorButton);
export const ToolbarEditorButtonWithoutTooltip = ToolBarEditorButton;

export function ToolbarEditorSplitButton({
  className,
  ...props
}: React.ComponentProps<typeof ToolBarEditorButton>) {
  return (
    <ToolbarEditorButtonWithoutTooltip
      className={cn('group flex gap-0 px-0 hover:bg-transparent', className)}
      {...props}
    />
  );
}

export function ToolbarEditorSplitButtonPrimary({
  children,
  className,
  size = 'sm',
  variant,
  ...props
}: React.ComponentProps<'span'> & VariantProps<typeof toolbarButtonVariants>) {
  return (
    <span
      className={cn(
        toolbarButtonVariants({
          size,
          variant,
        }),
        'rounded-r-none',
        'group-data-[pressed=true]:bg-accent group-data-[pressed=true]:text-accent-foreground',
        className,
      )}
      {...props}
    >
      {children}
    </span>
  );
}

export function ToolbarEditorToggleItem({
  className,
  size = 'sm',
  variant,
  ...props
}: React.ComponentProps<typeof ToolbarPrimitive.ToggleItem> &
  VariantProps<typeof toolbarButtonVariants>) {
  return (
    <ToolbarPrimitive.ToggleItem
      className={cn(toolbarButtonVariants({ size, variant }), className)}
      {...props}
    />
  );
}

export function ToolbarEditorGroup({
  children,
  className,
}: Readonly<{
  children?: React.ReactNode;
  className?: string;
}>) {
  return (
    <div className={cn('group/toolbar-group', 'relative hidden has-[button]:flex', className)}>
      <div className="flex items-center">{children}</div>
      <div className="group-last/toolbar-group:hidden! mx-1.5 py-0.5">
        <Separator className={'separator'} orientation="vertical" />
      </div>
    </div>
  );
}

type WithTooltipProps<T extends React.ElementType> = {
  tooltip?: React.ReactNode;
  tooltipContentProps?: Omit<React.ComponentProps<typeof TooltipEditorContent>, 'children'>;
  tooltipProps?: Omit<React.ComponentProps<typeof TooltipEditor>, 'children'>;
  tooltipTriggerProps?: React.ComponentProps<typeof TooltipEditorTrigger>;
} & React.ComponentProps<T>;

function withTooltip<T extends React.ElementType>(Component: T) {
  return function ExtendComponent({
    tooltip,
    tooltipContentProps,
    tooltipProps,
    tooltipTriggerProps,
    ...props
  }: WithTooltipProps<T>) {
    const [mounted, setMounted] = React.useState(false);

    React.useEffect(() => {
      setMounted(true);
    }, []);

    // TypeScript cannot match a generic component's props after removing the tooltip ones.
    const component = <Component {...(props as React.ComponentProps<T>)} />;

    if (tooltip && mounted) {
      return (
        <TooltipEditorProvider>
          <TooltipEditor {...tooltipProps}>
            <TooltipEditorTrigger asChild {...tooltipTriggerProps}>
              {component}
            </TooltipEditorTrigger>
            <TooltipEditorContent {...tooltipContentProps}>{tooltip}</TooltipEditorContent>
          </TooltipEditor>
        </TooltipEditorProvider>
      );
    }

    return component;
  };
}

function TooltipEditorContent({
  children,
  className,

  // CHANGE
  sideOffset = 4,

  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content>) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        className={cn(
          'z-50 w-fit origin-(--radix-tooltip-content-transform-origin) text-balance rounded-md bg-primary px-3 py-1.5 text-primary-foreground text-xs',
          className,
        )}
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        {...props}
      >
        {children}
        {/* CHANGE */}
        {/* <TooltipPrimitive.Arrow className="z-50 size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px] bg-primary fill-primary" /> */}
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
}

export function ToolbarEditorMenuGroup({
  children,
  className,
  label,
  ...props
}: React.ComponentProps<typeof DropdownMenuRadioGroup> & { label?: React.ReactNode }) {
  return (
    <>
      <DropdownMenuSeparator
        className={cn(
          'hidden',
          'mb-0 shrink-0 peer-has-[[role=menuitem]]/menu-group:block peer-has-[[role=menuitemradio]]/menu-group:block peer-has-[[role=option]]/menu-group:block',
        )}
      />
      <DropdownMenuRadioGroup
        {...props}
        className={cn(
          'hidden',
          'peer/menu-group group/menu-group my-1.5 has-[[role=menuitem]]:block has-[[role=menuitemradio]]:block has-[[role=option]]:block',
          className,
        )}
      >
        {label && (
          <DropdownMenuLabel className="select-none font-semibold text-muted-foreground text-xs">
            {label}
          </DropdownMenuLabel>
        )}
        {children}
      </DropdownMenuRadioGroup>
    </>
  );
}
